import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import VknLookupField from "@/components/VknLookupField";
import DefinitionPicker from "@/components/DefinitionPicker";
import { PROVINCES, districtsOf } from "@/lib/turkey_provinces";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Loader2, Building2, Search, ArrowRight, X, ChevronLeft, ChevronRight, MapPin, Phone, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import useViewMode from "@/hooks/useViewMode";
import ViewToggle from "@/components/ViewToggle";
import { useAuth } from "@/context/AuthContext";
import BhCustomerImportDialog from "@/components/BhCustomerImportDialog";
import ExcelPartyImportDialog from "@/components/ExcelPartyImportDialog";


const empty = {
  name: "",
  contact_person: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  district: "",
  tax_no: "",
  tax_office: "",
  brand_names: [],
  category_names: [],
};
const PAGE_SIZE = 500;

const pageList = (cur, total) => {
  const set = new Set([1, total, cur - 1, cur, cur + 1].filter((p) => p >= 1 && p <= total));
  const out = [];
  [...set].sort((a, b) => a - b).forEach((p, i, arr) => {
    if (i > 0 && p - arr[i - 1] > 1) out.push("…");
    out.push(p);
  });
  return out;
};

export default function Customers() {
  const { can } = useAuth();
  const canBhImport = can("customers", "add");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bhImportOpen, setBhImportOpen] = useState(false);
  const [excelImportOpen, setExcelImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [page, setPage] = useState(0);
  const [view, setView] = useViewMode("customers", "list");
  const searchInputRef = useRef(null);
  const rowRefs = useRef({});
  const navigate = useNavigate();

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paged = items;

  useEffect(() => {
    const t = setTimeout(() => {
      const q = search.trim();
      if (q === debounced) return;
      setDebounced(q); setPage(0); setHighlight(0);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  useEffect(() => {
    if (highlight >= paged.length) setHighlight(Math.max(0, paged.length - 1));
  }, [paged.length, highlight]);

  // Autofocus search on mount
  useEffect(() => {
    const t = setTimeout(() => searchInputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  // Global "/" shortcut re-focuses the search input from anywhere on the page.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = e.target;
        const editable = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
        if (!editable) {
          e.preventDefault();
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const onSearchKey = (e) => {
    if (paged.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(paged.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = paged[highlight] || paged[0];
      if (target) navigate(`/musteri/${target.id}`);
    } else if (e.key === "Escape") {
      setSearch("");
    } else if (e.key === "PageDown") {
      e.preventDefault();
      setPage((p) => Math.min(totalPages - 1, p + 1));
    } else if (e.key === "PageUp") {
      e.preventDefault();
      setPage((p) => Math.max(0, p - 1));
    }
  };

  // Scroll highlighted row into view
  useEffect(() => {
    const el = paged[highlight] && rowRefs.current[paged[highlight].id];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlight, paged]);

  const reqSeq = useRef(0);
  const load = async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data } = await api.get("/customers/paged", {
        params: { page: page + 1, limit: PAGE_SIZE, q: debounced },
      });
      if (seq !== reqSeq.current) return;
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debounced]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Müşteri eklendi");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Müşteri silindi");
      setItems((prev) => prev.filter((c) => c.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        title="Müşteri Yönetimi"
        subtitle="Toptan iş kıyafeti ve KKD müşterilerini yönetin"
        breadcrumbs={[]}
        actions={
          <div className="flex items-center gap-2">
            {canBhImport && (
              <Button
                data-testid="excel-import-open-btn"
                onClick={() => setExcelImportOpen(true)}
                variant="outline"
                className="rounded-sm uppercase tracking-wider text-xs font-semibold border-slate-700 hover:border-blue-600 hover:text-blue-400 transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel'den Müşteri Aktar
              </Button>
            )}
            {canBhImport && (
              <Button
                data-testid="bh-import-open-btn"
                onClick={() => setBhImportOpen(true)}
                variant="outline"
                className="rounded-sm uppercase tracking-wider text-xs font-semibold border-slate-700 hover:border-blue-600 hover:text-blue-400 transition-colors"
              >
                <Building2 className="w-4 h-4 mr-2" /> Bizim Hesap'tan Aktar
              </Button>
            )}
            <Button
              data-testid="add-customer-btn"
              onClick={() => setOpen(true)}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" /> Yeni Müşteri
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              ref={searchInputRef}
              data-testid="customer-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Ara: firma, yetkili, telefon, e-posta, vergi no…  (/ ile fokus)"
              className="bg-slate-900 border-slate-700 rounded-sm focus-visible:ring-blue-500 pl-10 pr-9 h-10"
            />
            {search && (
              <button
                type="button"
                data-testid="customer-search-clear"
                onClick={() => { setSearch(""); searchInputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-red-400 p-1"
                title="Temizle (Esc)"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="text-[11px] font-mono text-slate-500" data-testid="customer-result-count">
            {loading ? "" : (
              debounced
                ? `${total} eşleşen`
                : `${total} müşteri`
            )}
          </div>
          <div className="hidden md:flex items-center gap-1 text-[10px] uppercase font-mono text-slate-600 ml-auto">
            <kbd className="px-1.5 py-0.5 border border-slate-700 rounded-sm">↑↓</kbd>
            <span>gez</span>
            <kbd className="px-1.5 py-0.5 border border-slate-700 rounded-sm ml-2">Enter</kbd>
            <span>aç</span>
            <kbd className="px-1.5 py-0.5 border border-slate-700 rounded-sm ml-2">/</kbd>
            <span>ara</span>
          </div>
          <ViewToggle value={view} onChange={setView} testId="customer-view" />
        </div>
        <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Building2 className="w-10 h-10 mx-auto text-slate-700 mb-3" />
              <div className="text-slate-400 text-sm">
                {search ? "Aramaya uygun müşteri bulunamadı." : "Henüz müşteri yok. İlkini ekleyerek başlayın."}
              </div>
            </div>
          ) : view === "grid" ? (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="customer-cards">
              {paged.map((c, idx) => (
                <div
                  key={c.id}
                  ref={(el) => { rowRefs.current[c.id] = el; }}
                  data-testid={`customer-card-${c.id}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => navigate(`/musteri/${c.id}`)}
                  className={`group relative rounded-sm border p-3 cursor-pointer transition-colors ${
                    idx === highlight
                      ? "bg-blue-950/30 border-blue-800/60"
                      : "bg-slate-950/40 border-slate-800 hover:bg-slate-900/60 hover:border-slate-700"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-100 truncate">{c.name}</div>
                      {c.contact_person && (
                        <div className="text-[11px] text-slate-400 truncate">{c.contact_person}</div>
                      )}
                    </div>
                    <Button
                      data-testid={`delete-customer-card-${c.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                      className="text-slate-600 hover:text-red-400 hover:bg-red-950/30 rounded-sm p-1 h-auto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="space-y-1 text-[11px]">
                    {c.phone && (
                      <div className="flex items-center gap-1.5 text-slate-400 font-mono">
                        <Phone className="w-3 h-3" /> {c.phone}
                      </div>
                    )}
                    {(c.city || c.district) && (
                      <div className="flex items-center gap-1.5 text-slate-400 font-mono">
                        <MapPin className="w-3 h-3" /> {c.city}{c.district && ` · ${c.district}`}
                      </div>
                    )}
                    {c.tax_no && (
                      <div className="text-slate-500 font-mono">VKN: {c.tax_no}</div>
                    )}
                  </div>
                  {((c.brand_names || []).length > 0 || (c.category_names || []).length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-800">
                      {(c.brand_names || []).slice(0, 2).map((t, i) => (
                        <span key={`b-${i}`} className="text-[9px] px-1.5 py-0.5 border border-emerald-900/60 text-emerald-300 bg-emerald-950/30 rounded-sm font-mono">
                          {t}
                        </span>
                      ))}
                      {(c.category_names || []).slice(0, 2).map((t, i) => (
                        <span key={`c-${i}`} className="text-[9px] px-1.5 py-0.5 border border-sky-900/60 text-sky-300 bg-sky-950/30 rounded-sm font-mono">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <ArrowRight className="w-3.5 h-3.5 absolute bottom-2 right-2 text-slate-700 group-hover:text-blue-500 transition-colors" />
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-800">
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Müşteri</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">İletişim</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Telefon</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Konum</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Marka / Kategori</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Vergi No</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((c, idx) => (
                  <TableRow
                    key={c.id}
                    ref={(el) => { rowRefs.current[c.id] = el; }}
                    className={`border-slate-800 cursor-pointer group ${
                      idx === highlight
                        ? "bg-blue-950/30 hover:bg-blue-950/40 outline outline-1 outline-blue-800/50"
                        : "hover:bg-slate-800/40"
                    }`}
                    data-testid={`customer-row-${c.id}`}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => navigate(`/musteri/${c.id}`)}
                  >
                    <TableCell className="font-medium text-slate-100">{c.name}</TableCell>
                    <TableCell className="text-slate-300">{c.contact_person || "-"}</TableCell>
                    <TableCell className="text-slate-400 font-mono text-xs">{c.phone || "-"}</TableCell>
                    <TableCell className="text-xs">
                      {(c.city || c.district) ? (
                        <div className="text-slate-300 font-mono">
                          {c.city}
                          {c.district && <span className="text-slate-500"> · {c.district}</span>}
                        </div>
                      ) : <span className="text-slate-600">-</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(c.brand_names || []).slice(0, 3).map((t, i) => (
                          <span key={`b-${i}`} className="text-[10px] px-1.5 py-0.5 border border-emerald-900/60 text-emerald-300 bg-emerald-950/30 rounded-sm font-mono">
                            {t}
                          </span>
                        ))}
                        {(c.category_names || []).slice(0, 3).map((t, i) => (
                          <span key={`c-${i}`} className="text-[10px] px-1.5 py-0.5 border border-sky-900/60 text-sky-300 bg-sky-950/30 rounded-sm font-mono">
                            {t}
                          </span>
                        ))}
                        {((c.brand_names || []).length + (c.category_names || []).length) === 0 && (
                          <span className="text-slate-600 text-xs">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-400 font-mono text-xs">{c.tax_no || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {idx === highlight && (
                          <span className="text-[9px] uppercase font-mono text-blue-400 mr-1">Enter</span>
                        )}
                        <ArrowRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-blue-500 transition-colors" />
                        <Button
                          data-testid={`delete-customer-${c.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                          className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {!loading && total > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-slate-400 font-mono" data-testid="customer-pagination">
            <div data-testid="customer-pagination-summary">
              Toplam {total} müşteri · {totalPages} sayfa · Sayfa {page + 1}
            </div>
            <div className="flex items-center gap-1">
              <Button
                data-testid="customer-page-prev"
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="h-8 px-2 rounded-sm text-slate-300 hover:bg-slate-800 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" /> Önceki
              </Button>
              {pageList(page + 1, totalPages).map((p, i) => p === "…" ? (
                <span key={`e-${i}`} className="px-1 text-slate-600">…</span>
              ) : (
                <Button
                  key={p}
                  data-testid={`customer-page-${p}`}
                  size="sm"
                  variant="ghost"
                  onClick={() => setPage(p - 1)}
                  className={`h-8 min-w-8 px-2 rounded-sm ${p === page + 1 ? "bg-blue-600 text-white hover:bg-blue-500" : "text-slate-300 hover:bg-slate-800"}`}
                >
                  {p}
                </Button>
              ))}
              <Button
                data-testid="customer-page-next"
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="h-8 px-2 rounded-sm text-slate-300 hover:bg-slate-800 disabled:opacity-30"
              >
                Sonraki <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">Yeni Müşteri</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Firma Adı *</Label>
              <Input
                data-testid="customer-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Yetkili</Label>
                <Input
                  value={form.contact_person}
                  onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Telefon</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">E-posta</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Vergi No</Label>
                <VknLookupField
                  value={form.tax_no}
                  onChange={(v) => setForm({ ...form, tax_no: v })}
                  onCompanyFound={(c) => setForm((p) => ({ ...p, name: p.name || c.title }))}
                  testId="customer-tax-no"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Vergi Dairesi</Label>
                <Input
                  data-testid="customer-tax-office"
                  value={form.tax_office || ""}
                  onChange={(e) => setForm({ ...form, tax_office: e.target.value })}
                  placeholder="örn: KADIKÖY"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">İl</Label>
                <Select
                  value={form.city || "__none__"}
                  onValueChange={(v) => setForm({ ...form, city: v === "__none__" ? "" : v, district: "" })}
                >
                  <SelectTrigger data-testid="customer-city" className="bg-slate-950 border-slate-700 rounded-sm focus:ring-blue-500">
                    <SelectValue placeholder="İl seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— İl Seçilmedi —</SelectItem>
                    {PROVINCES.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                  İlçe
                  {!form.city && (
                    <span className="ml-1 text-[10px] normal-case font-sans text-slate-600">
                      · önce il seçin
                    </span>
                  )}
                </Label>
                <Select
                  disabled={!form.city}
                  value={form.district || "__none__"}
                  onValueChange={(v) => setForm({ ...form, district: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="customer-district" className="bg-slate-950 border-slate-700 rounded-sm focus:ring-blue-500 disabled:opacity-40">
                    <SelectValue placeholder={form.city ? "İlçe seçin" : "—"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— İlçe Seçilmedi —</SelectItem>
                    {districtsOf(form.city).map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Adres</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
                placeholder="Cadde, sokak, no, posta kodu…"
                className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none"
              />
            </div>
            <div className="pt-2 border-t border-slate-800 space-y-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                Müşteri Segmentasyonu (CRM)
              </div>
              <DefinitionPicker
                kind="brand"
                label="İlgilendiği Markalar"
                value={form.brand_names}
                onChange={(names) => setForm({ ...form, brand_names: names })}
                testIdPrefix="customer-brand-picker"
                placeholder="Marka ara…"
              />
              <DefinitionPicker
                kind="product_category"
                label="İlgilendiği Ürün Kategorileri"
                value={form.category_names}
                onChange={(names) => setForm({ ...form, category_names: names })}
                testIdPrefix="customer-category-picker"
                placeholder="Kategori ara…"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
              >
                İptal
              </Button>
              <Button
                data-testid="save-customer-btn"
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BhCustomerImportDialog
        open={bhImportOpen}
        onClose={() => setBhImportOpen(false)}
        onImported={load}
      />
      <ExcelPartyImportDialog
        kind="customers"
        open={excelImportOpen}
        onClose={() => setExcelImportOpen(false)}
        onImported={load}
      />
    </div>
  );
}
